firebird.factory('videoReconnectService', [
    '$rootScope',
    '$timeout',
    '$window',
    'logService',
    'helperService',
    'configService',
    'notificationService',
    'localizationService',
    'avDeviceService',
    'storageService',
    'attendeeService',
    'attendeeListService',
    'analyticsWrapperService',
    'videoGlobalService',
    'executionEnvironmentEnum',
    'socketService',
    function (
        $rootScope,
        $timeout,
        $window,
        logService,
        helperService,
        configService,
        notificationService,
        localizationService,
        avDeviceService,
        storageService,
        attendeeService,
        attendeeListService,
        analyticsWrapperService,
        videoGlobalService,
        executionEnvironmentEnum,
        socketService
    ) {
        var service = {
            initialize: initialize
        };

        var self, $scope;

        const logger = logService.createLoggerWithSource('[videoroomCtrl] videoReconnectService');

        return service;

        function initialize(selfParam) {
            self = selfParam;
            $scope = videoGlobalService.data.$scope;

            if (videoGlobalService.test) {
                $window = videoGlobalService.data.$window;
            }

            self.initReconnect = initReconnect;

            self.rejoinConferenceTimer = null;
            self.networkMoreStatsTimer = null;
            self.waitForConnectivityCheck = false;
            self.peerConnectionStatsTimer = null;
            self.initNetworkState = initNetworkState;
            self.prepareForRejoinConference = prepareForRejoinConference;
            self.rejoinConference = rejoinConference;
            self.rejoinConferenceIfNeeded = rejoinConferenceIfNeeded;
            self.rejoinConferenceAfter = rejoinConferenceAfter;

            // when disconnected from node for 20s, reset Janus state
            self.nodeReconnectTimer = null;
            self.nodeReconnectTimeout = 20000;
            self.nodeReconnectJoin = false;
            self.nodeReconnectJoinTimeout = null;

            self.fastReconnectSocket = {
                enabled: false,
                reconnect: true, // use false for logging only
                disconnectTs: null,
                connectTs: null,
                fastTs: null
            };

            self.iceRestartTimeout = 4000;
            self.iceRestartAfter = iceRestartAfter;
            self.iceRestartInternal = iceRestartInternal;
            self.iceRestartCancel = iceRestartCancel;
            self.iceRestartCancelAll = iceRestartCancelAll;

            self.disconnectRetry = 0;
            self.disconnectMaxRetries = 10;
            self.disconnectMaxRetriesSIP = 6;
            self.enableReclaim = true; // whether to do Janus session reclaim or not?
            self.enableReclaimTimeout = 60000; // 5000 if enableReclaimRetry is not set.
            self.reclaimOnLostWebsocket = true; // whether to do Janus session reclaim or full rejoin in case of lost connection to media server
            self.enableReclaimRetry = true;

            self.cleanupRetryMax = 2; // how many times to retry create peer connection when unrequested cleanup is done.

            self.reconnectOnMute = false;

            $scope.$on('node-disconnected', onNodeDisconnected);
            $scope.$on('node-reconnected', onNodeReconnected);

            self.iceRestartState = {}; // index is type or type-id and value is object {time, count, ...}
            self.checkIceTransportOnTCP = checkIceTransportOnTCP;
            self.resetIceRestartState = resetIceRestartState;

            self.iceRestartOnAudio = 0; // whether to restart others when audio connection is restarted, and interval. set 0 to disable. ANYAM-73782
            self.iceRestartOnAudioTimeout = null; // timer instance.
            self.iceRestartOnAudioLimit = 12; // if more than 12 restarts are needed, then avoid restarting any video-receive connections.

            self.iceMediaInitiated = false; // indicates that media was flowing in any peer connection.

            self.networkState = {
                enabled: true, // whether to allow rejoin conference after network restored
                enableRejoinAfterNetworkConditionsChanged: true, // whether to allow rejoin conference after network changed
                missingType: false, // missing connection or type feature
                restorePeriod: 5000, // 5 seconds: restore period for network, downlink and rtt
                rejoinAfter: 2000, // 2 seconds: delay before rejoin conference
                timeBetweenGetPCStats: 3000, // 3 seconds: time between get peer connection stats
                packetsLostThreshold: 3 * 15, // 3 seconds * 30% packetsLost (we have an average of 50 packets per second)
                availableOutgoingBitrateThreshold: 300000, // 300 kbit/s
                downlinkThresholdAffectsQuality: 2, // 2 mbit/s
                rttThreshold: 200, // 200 ms
                rttLow: 100, // 100 ms: low range for rtt
                rttHigh: 200, // 200 ms: high range for rtt
                waitForRestore: false, // if set to true then rejoin conference after network restored
                previousConnectionType: null,
                previousConnectionTypeChangeTime: null,
                previousDownlink: null,
                previousDownlinkChangeTime: null,
                previousRtt: null,
                previousRttChangeTime: null,
                previousEffectiveType: null,
                allowedStatsTypes: null, // allowed peer connections for get stats (audio|videoroom|screenshare)
                allowedStatsMetrics: null, // bytesReceived|availableOutgoingBitrate|framesEncoded|packetsLost|rtt
                fakePrevStats: {
                    bytesReceived: -1,
                    availableOutgoingBitrate: -1,
                    framesEncoded: -1,
                    packetsLost: -1,
                    rtt: -1
                },
                fakeNewStats: {
                    bytesReceived: -1,
                    availableOutgoingBitrate: -1,
                    framesEncoded: -1,
                    packetsLost: -1,
                    rtt: -1
                },
                moreStats: {
                    collectAfter: 3000, // 3 seconds
                    enableCheckConnectivity: false, // whether to allow check connectivity
                    enableSpeedtest: false, // whether to allow speed test
                    connectivityDelayThreshold: 1000, // 1 second
                    speedtestBwDownThreshold: 1200 // 1.2 mbit/s
                },
                media: {
                    trackDuration: true,
                    durationThreshold: 4000 // 4 seconds
                }
            };

            self.onNetworkStateChange = onNetworkStateChange;
            self.onNetworkChange = onNetworkChange;
            self.isNetworkConnectionTypeRestored = isNetworkConnectionTypeRestored;
            self.isNetworkDownlinkRestored = isNetworkDownlinkRestored;
            self.isNetworkRttRestored = isNetworkRttRestored;
            self.isNetworkConditionsChanged = isNetworkConditionsChanged;
            self.isNetworkDownlinkChangedSignificantly = isNetworkDownlinkChangedSignificantly;
            self.isNetworkRTTChangedSignificantly = isNetworkRTTChangedSignificantly;
            self.getNetworkConnectionTypeChangePeriod = getNetworkConnectionTypeChangePeriod;
            self.getNetworkDownlinkChangePeriod = getNetworkDownlinkChangePeriod;
            self.getNetworkRttChangePeriod = getNetworkRttChangePeriod;
            self.getNetworkRttRange = getNetworkRttRange;
            self.getPeerConnectionForStats = getPeerConnectionForStats;
            self.cancelPeerConnectionStats = cancelPeerConnectionStats;
            self.analyzePeerConnectionStatsAndRejoinConferenceIfNeeded =
                analyzePeerConnectionStatsAndRejoinConferenceIfNeeded;
            self.networkCollectMoreStatsAfter = networkCollectMoreStatsAfter;
            self.networkCancelMoreStatsAfter = networkCancelMoreStatsAfter;
            self.tryRejoinConferenceAfterNetworkChange = tryRejoinConferenceAfterNetworkChange;
            self.updatePreviousNetworkState = updatePreviousNetworkState;

            if ($window.navigator.connection) {
                $window.navigator.connection.onchange = self.onNetworkChange;
            }

            if ($window.navigator.connection?.type === undefined) {
                // missing connection or type feature.
                self.networkState.missingType = true;
                $window.addEventListener('online', self.onNetworkStateChange);
                $window.addEventListener('offline', self.onNetworkStateChange);
            }

            self.fixSwitchToAllRelay = {
                enabled: false,
                count: 0,
                maxCount: 5, // 5 consecutive stats cycle to switch to relay
                threshold: 0.175, // at least 17.5% using floor of relay
                // example: M relay in N, causes switch. M=0 if N=0-5, M=1 if N=6-11, M=3 if N=12-17, …
                useRelay: false // once set it remains on
            };

            self.checkSwitchToAllRelay = checkSwitchToAllRelay;
        }

        // update state from config
        function initReconnect() {
            // enableReclaim - default true, can be turned off by url params
            var enableReclaim = configService.getClientValue('enableReclaim');
            if (enableReclaim) {
                self.enableReclaim = enableReclaim !== 'false';
            }

            var enableReclaimRetry = configService.getValue('enableReclaimRetry');
            self.enableReclaimRetry = !enableReclaimRetry || enableReclaimRetry !== 'false';
            self.enableReclaimTimeout = self.enableReclaimRetry ? 60000 : 5000;

            // enableReclaimTimeout - default is 5 second, can be changed by url params
            const reclaimTimeout = parseInt(configService.getClientValue('enableReclaimTimeout'));
            if (!isNaN(reclaimTimeout) && reclaimTimeout > 0) {
                self.enableReclaimTimeout = reclaimTimeout;
            }

            var iceRestartOnAudio = configService.getValue('iceRestartOnAudio') || '0';
            if (iceRestartOnAudio == 'true') {
                // fallback for convenience
                iceRestartOnAudio = '10000'; // default
            } else if (iceRestartOnAudio == 'false') {
                iceRestartOnAudio = '0';
            }
            self.iceRestartOnAudio = parseInt(iceRestartOnAudio) || 0;
            if (self.iceRestartOnAudio) {
                var indexOther1 = (iceRestartOnAudio || '').indexOf(',');
                if (indexOther1 > 0) {
                    self.iceRestartOnAudioLimit = parseInt(iceRestartOnAudio.split(',')[1]) || 12;
                }
                logger.log('videoroomCtrl using iceRestartOnAudio ' + self.iceRestartOnAudio);
            }

            // reconnectOnMute - default false.
            self.reconnectOnMute = configService.getClientValue('reconnectOnMute') == 'true';
            configService.setConfigObjectFromName(self.fastReconnectSocket, 'fastReconnectSocket');
            configService.setConfigObjectFromName(self.fixSwitchToAllRelay, 'fixSwitchToAllRelay');
            self.initNetworkState();
        }

        function initNetworkState() {
            const initialNetworkState = { ...self.networkState };
            const configuredNetworkState = configService.getValue('networkState');

            logger.log('initNetworkState', 'set network state parameters', initialNetworkState, configuredNetworkState);

            if (configuredNetworkState == 'false') {
                self.networkState.enabled =
                    self.networkState.enableRejoinAfterNetworkConditionsChanged =
                    self.networkState.moreStats.enableCheckConnectivity =
                    self.networkState.moreStats.enableSpeedtest =
                    self.networkState.media.trackDuration =
                        false;
            } else if (configuredNetworkState != 'true') {
                if (configuredNetworkState) {
                    configService.setConfigObjectFromCSV(self.networkState, configuredNetworkState);

                    if (typeof self.networkState.allowedStatsTypes === 'string') {
                        self.networkState.allowedStatsTypes = self.networkState.allowedStatsTypes.split('|');
                    }

                    if (typeof self.networkState.allowedStatsMetrics === 'string') {
                        self.networkState.allowedStatsMetrics = self.networkState.allowedStatsMetrics.split('|');
                    }
                }
            }

            if (self.networkState.missingType !== initialNetworkState.missingType) {
                if (self.networkState.missingType) {
                    $window.addEventListener('online', self.onNetworkStateChange);
                    $window.addEventListener('offline', self.onNetworkStateChange);
                } else {
                    $window.removeEventListener('online', self.onNetworkStateChange);
                    $window.removeEventListener('offline', self.onNetworkStateChange);
                }
            }
        }

        function prepareForRejoinConference() {
            if (self.screenshare) {
                logger.log('prepareForRejoinConference', 'saving screen share stream for reconnection');
                self.saveScreenshareForReconnection();
                self.stopScreenShare(false, 'will rejoin');
            }

            self.iceRestartCancelAll();
            logger.log('prepareForRejoinConference', 'ICE restart timers canceled');
        }

        // re-join a video room. it stops the local streams, increments the retry
        // count, and invokes joinConference.
        function rejoinConference(reason) {
            logger.log('rejoinConference', `reason=${reason}`);

            analyticsWrapperService.eventTrack('Meeting - rejoin conference', { reason });
            self.msgflowEvent('rejoin:' + (reason || 'unknown'));

            self.leaveConferenceAlt('rejoin');
            self.checkConnectivity('rejoinConference');
            self.joinConference();
        }

        function rejoinConferenceIfNeeded(reason) {
            if (self.hasTestLayout) {
                self.hideTestLayout();
                self.hasTestLayout = false;
            }
            if (self.showTestLayout()) {
                self.hasTestLayout = true;
            } else {
                if (self.screenshare && self.screenshareState != 'disconnected' && self.screenshareState != 'stopped') {
                    self.saveScreenshareForReconnection();
                    self.stopScreenShare();
                }
                logger.log('rejoinConferenceIfNeeded', 'Rejoining conference');
                self.rejoinConference(reason);
            }
        }

        // call rejoin conference (or ifneeded) after a delay (may be 0).
        // if connectivity check is needed, then after connectivity check.
        // In which case delay is used to limit connectivity check duration.
        // if node websocket was disconnected and waiting for reconnect (leavealt) then do not rejoin.
        function rejoinConferenceAfter(reason, delay, doConnectivityCheck, ifNeeded, callbackBefore) {
            logger.log(
                'rejoinConferenceAfter',
                `reason=${reason}; delay=${delay}; doConnectivityCheck=${doConnectivityCheck}; ifNeeded=${ifNeeded}; has callbackBefore=${!!callbackBefore};`
            );

            if (self.rejoinConferenceTimer) {
                $timeout.cancel(self.rejoinConferenceTimer);
                self.rejoinConferenceTimer = null;
            }

            self.stopCheckConnectivity();
            self.cancelPeerConnectionStats();

            if (self.conferenceLeft) {
                logger.log('rejoinConferenceAfter', 'conferenceLeft true, returning from rejoinConferenceAfter');
                return;
            }

            if (doConnectivityCheck) {
                if (!self.waitForConnectivityCheck) {
                    self.waitForConnectivityCheck = true;
                    self.checkConnectivity('rejoinConferenceAfter', delay, function () {
                        if (self.conferenceLeft) {
                            logger.log(
                                'rejoinConferenceAfter',
                                'conferenceLeft true, returning from rejoinConferenceAfter'
                            );
                            return;
                        }

                        if (callbackBefore) {
                            callbackBefore();
                        }

                        if (self.waitForConnectivityCheck) {
                            self.waitForConnectivityCheck = false;

                            if (!self.nodeReconnectJoin) {
                                ifNeeded ? self.rejoinConferenceIfNeeded(reason) : self.rejoinConference(reason);
                            }
                        } // else leave was called.
                    });
                } // else already scheduled another rejoin.
            } else {
                self.updateConnTimeData('mediaSocket', false);

                self.rejoinConferenceTimer = $timeout(function () {
                    self.rejoinConferenceTimer = null;

                    if (self.conferenceLeft) {
                        logger.log(
                            'rejoinConferenceAfter',
                            'conferenceLeft true, returning from rejoinConferenceAfter'
                        );
                        return;
                    }

                    if (callbackBefore) {
                        callbackBefore();
                    }

                    if (!self.waitForConnectivityCheck && !self.nodeReconnectJoin) {
                        ifNeeded ? self.rejoinConferenceIfNeeded(reason) : self.rejoinConference(reason);
                    } // else waiting for connectivity check to complete.
                }, delay);
            }
        }

        // schedule iceRestart after some timeout (4s).
        function iceRestartAfter(context, type, arg, timeout) {
            if (helperService.getIsMeetingEnding()) {
                logger.log('videoroomCtrl meeting ending... returning from iceRestartAfter');
                return;
            }

            self.iceRestartCancel(context);
            self.msgflowEvent(timeout ? 'ice-restart:forced' : 'ice-restart:scheduled', {
                type: type,
                id: arg,
                timeout: timeout
            });

            if (context) {
                context.iceRestartTimer = self.layoutPageTimeoutWithState(
                    { context: context, arg: arg },
                    function (state) {
                        var context = state.context,
                            arg = state.arg;
                        delete context.iceRestartTimer;

                        if (!context.state || context.state.ice != 'disconnected') {
                            logger.log(
                                'videoroomCtrl iceRestartAfter no longer disconnected type=' + type + ' arg=' + arg
                            );
                            return;
                        }

                        if (type == 'video-receive') {
                            self.msgflowEvent('play-restart:ice', {
                                id: arg,
                                reason: 'ice restart after ' + (timeout || 'default')
                            });
                        } else {
                            self.msgflowEvent('ice-restart:init', { type: type, id: arg, timeout: timeout });
                        }

                        var valid = false; // whether to continue or if plugin changed then not.
                        if (type == 'video-send') {
                            if (self.videoroom == context) {
                                logger.log('videoroomCtrl iceRestartAfter ice restart for video publish');
                                valid = true;
                            }
                        } else if (type == 'screen-send') {
                            if (self.screenshare == context) {
                                logger.log('videoroomCtrl iceRestartAfter ice restart for screen video publish');
                                valid = true;
                            }
                        } else if (type == 'video-receive') {
                            var id = arg;
                            if (typeof self.feeds[id] === 'object' && self.feeds[id] == context) {
                                logger.log('videoroomCtrl iceRestartAfter restarting video subscription:' + id);
                                valid = true;
                            }
                        } else if (type == 'audio') {
                            var is_screen = !!arg;
                            if (
                                (!is_screen && self.sipcall == context) ||
                                (is_screen && self.screensipcall == context)
                            ) {
                                logger.log('videoroomCtrl iceRestartAfter ice restart for sip, is_screen=' + is_screen);
                                valid = true;
                            }
                        }

                        if (valid) {
                            if (!self.checkIceTransportOnTCP(context, type, arg)) {
                                // if true, do full reconnect, else continue with restart
                                self.iceRestartInternal(type, arg);
                            }
                        }
                    },
                    timeout || self.iceRestartTimeout
                );

                self.checkConnectivity('iceRestartAfter');
            }
        }

        function iceRestartInternal(type, arg) {
            if (self.VDIRef.getVdiType() === executionEnvironmentEnum.Citrix) {
                // On VDI - The user does not reconnect back to the meeting after a network reconnection as the sdk redirection gets suspended.
                // Hence the need to rejoin conference after network reconnection.
                logger.log('videoroomCtrl VDI - rejoin instead of iceRestart');
                self.rejoinConferenceIfNeeded('VDI restart');
                return;
            }
            if (type == 'video-send') {
                self.createOfferOrRepublish(false, null, true, true);
            } else if (type == 'screen-send') {
                if (!self.fileplay && self.fixIceRestartForScreen == 'disable') {
                    if (
                        self.screenshare &&
                        self.screenshareState != 'disconnected' &&
                        self.screenshareState != 'stopped'
                    ) {
                        self.saveScreenshareForReconnection();
                        self.stopScreenShare(false, 'ice restart');
                        self.startScreenShare();
                    }
                } else {
                    self.createOfferForScreen(true);
                }
            } else if (type == 'video-receive') {
                self.msgflowEvent('play-restart:ice', { reason: 'for ice-restart', id: arg });
                self.restartVideoPlay(arg);
            } else if (type == 'audio') {
                self.createOfferForSIP(!!arg, true);
            }
        }

        // cancel iceRestart timer if any.
        function iceRestartCancel(context) {
            if (context && context.iceRestartTimer) {
                logger.log('videoroomCtrl canceling iceRestart timer');
                self.layoutPageTimeoutCancelWithState(context.iceRestartTimer);
                delete context.iceRestartTimer;
            }
        }

        // cancel iceRestart on all plugin handles.
        function iceRestartCancelAll() {
            if (self.videoroom) {
                self.iceRestartCancel(self.videoroom);
            }
            if (self.screenshare) {
                self.iceRestartCancel(self.screenshare);
            }
            if (self.sipcall) {
                self.iceRestartCancel(self.sipcall);
            }
            if (self.screensipcall) {
                self.iceRestartCancel(self.screensipcall);
            }
            for (var feedId in self.feeds) {
                if (typeof self.feeds[feedId] === 'object') {
                    self.iceRestartCancel(self.feeds[feedId]);
                }
            }
        }

        // return true if reconnect triggered, else false. In future it should keep count per plugin.
        // previously this was only invoked for SIP/audio plugin and video publish plugin, not for play or screen publish.
        // now it is invoked for all peer connections.
        function checkIceTransportOnTCP(plugin, type, arg) {
            var index = type + (type == 'video-receive' ? '-' + arg : '') + (type == 'audio' && arg ? '-screen' : '');
            var data = self.iceRestartState[index];
            if (data === undefined) {
                data = self.iceRestartState[index] = { count: 0, time: 0 }; // disconnected time and count.
            }

            var iceDone = plugin.webrtcStuff?.pc?._iceDone;

            if (
                !self.iceTransportSwitchedToTCP &&
                type != 'video-receive' &&
                !self.iceMediaInitiated &&
                self.iceTransportOnTCP > 1
            ) {
                self.msgflowEvent('no-media-flow', { iceDone, type });

                if (iceDone) {
                    self.iceTransportSwitchedToTCP = true;

                    logger.warn('joinConference', 'no media flow on disconnect, try to use TURN TCP 443');

                    self.iceTransportOnTCP = 1;
                    self.rejoinConferenceAfter('TCP 443 no media', 0, false, true);

                    analyticsWrapperService.eventTrack('Meeting - fallback to TURN TCP 443 - no media flow', {
                        sfuEvent: true
                    });

                    return true;
                } else {
                    logger.log(
                        'joinConference',
                        'no media flow on disconnect but not ice done, do not switch to TURN TCP'
                    );
                }
            }

            var now = Date.now();
            if (self.iceTransportOnTCP > 0 && data.count < self.iceTransportOnTCP - 1) {
                if (data.count > 0 && data.time && data.time < now - self.iceDisconnectedMaxDuration) {
                    logger.log('videoroomCtrl - resetting disconnected count as more than duration ago');
                    data.count = 0;
                }

                data.time = now; // reset the last disconnect time to now.
                data.count++;

                if (data.count == self.iceTransportOnTCP - 1) {
                    // reached the limit
                    logger.error(
                        'checkIceTransportOnTCP',
                        'rejoining conference because disconnected count reached limit',
                        data.count
                    );
                    self.iceDisconnectedCount = data.count; // this will trigger TCP TURN for next peer connection.
                    self.rejoinConferenceAfter('TCP 443', 0, false, true); // also restores screen share if any.
                    analyticsWrapperService.eventTrack('Meeting - fallback to TURN TCP 443', {
                        sfuEvent: true,
                        config: self.iceTransportOnTCP,
                        msgflow: 'rejoin:tcp443'
                    });
                    return true;
                } else {
                    logger.log(
                        'videoroomCtrl - disconnected count=' + data.count + ' limit=' + (self.iceTransportOnTCP - 1)
                    );
                }
            } else {
                data.time = now;
            }

            if (self.iceRestartOnAudio && type == 'audio' && !arg) {
                // audio connection restart
                var time0 = data.time;
                if (self.iceRestartOnAudioTimeout) {
                    $timeout.cancel(self.iceRestartOnAudioTimeout);
                    self.iceRestartOnAudioTimeout = null;
                }
                self.iceRestartOnAudioTimeout = $timeout(function () {
                    // after timeout, check if all others have recent restart or not.
                    self.iceRestartOnAudioTimeout = null;
                    var plugins = [];
                    var checkPlugin = function (plugin, type, arg) {
                        var index =
                            type +
                            (type == 'video-receive' ? '-' + arg : '') +
                            (type == 'audio' && arg ? '-screen' : '');
                        if (
                            plugin &&
                            !plugin.iceRestartTimer &&
                            (self.iceRestartState[index] === undefined ||
                                Math.abs(self.iceRestartState[index].time - time0) > self.iceRestartOnAudio)
                        ) {
                            plugins.push({ plugin: plugin, index: index, type: type, arg: arg });
                        }
                    };
                    checkPlugin(self.videoroom, 'video-send');
                    checkPlugin(self.screenshare, 'screen-send');
                    for (var fid in self.feeds) {
                        if (self.feeds[fid] !== true) {
                            checkPlugin(self.feeds[fid], 'video-receive', fid);
                        }
                    }
                    if (plugins.length > self.iceRestartOnAudioLimit) {
                        // too many restarts, avoid all rx.
                        logger.log(
                            'videoroomCtrl - too many restarts due to audio restart, ignoring video receive restarts'
                        );
                        plugins = plugins.filter(function (item) {
                            return item.type != 'video-receive';
                        });
                    }
                    var data = {
                        plugins: plugins.map(function (item) {
                            return item.index;
                        })
                    };
                    logger.log(
                        'videoroomCtrl - restarting ICE for other media, due to audio restart ' +
                            data.plugins.length +
                            ' ' +
                            data.plugins.join(',')
                    );
                    analyticsWrapperService.eventTrack('Meeting - ice restart all due to audio', {
                        sfuEvent: true,
                        count: data.plugins.length
                    });
                    self.msgflowEvent('ice-restart:all', data);
                    plugins.forEach(function (item) {
                        self.iceRestartInternal(item.type, item.arg);
                    });
                }, parseInt(self.iceRestartOnAudio) || 10000);
            }

            return false;
        }

        // if type is missing, reset all.
        function resetIceRestartState(type, arg) {
            if (!type) {
                self.iceRestartState = {}; // reset everything.
                if (self.iceRestartOnAudioTimeout) {
                    $timeout.cancel(self.iceRestartOnAudioTimeout);
                    self.iceRestartOnAudioTimeout = null;
                }
            } else {
                var index =
                    type + (type == 'video-receive' ? '-' + arg : '') + (type == 'audio' && arg ? '-screen' : '');
                delete self.iceRestartState[index];
                if (index == 'audio' && self.iceRestartOnAudioTimeout) {
                    $timeout.cancel(self.iceRestartOnAudioTimeout);
                    self.iceRestartOnAudioTimeout = null;
                }
            }
        }

        function onNodeDisconnected() {
            logger.log('videoroomCtrl on node disconnected, reset screen share');
            self.updateConnTimeData('nodeSocket', false);

            if (self.fastReconnectSocket.enabled) {
                self.fastReconnectSocket.disconnectTs = Date.now();
            }

            var oldFileplay = self.fileplay;
            var oldState = self.screenshareState;

            // set the state to disconnected before and restore state after,
            // so that cleanup does not reset fileplay state.

            self.screenshareState = 'disconnected';
            self.cleanUpScreenShareOwner();

            self.screenshareState = oldState;

            // if node remains disconnected for 20s, then disconnect Janus state.
            // or if ICE restart is happening for any plugin, then disconnect Janus state.
            var reconnectHandler = function (event) {
                if (event != 'immediate') {
                    logger.warn('onNodeDisconnected', 'leaving conference abruptly due to node reconnect timeout');
                    self.msgflowEvent('node disconnected:cleanup');
                } else {
                    logger.warn(
                        'onNodeDisconnected',
                        'leaving conference abruptly due to node disconnect during reconnection'
                    );
                }
                self.nodeReconnectTimer = null;
                self.nodeReconnectJoin = true;
                if (self.screenshare) {
                    logger.log('videoroomCtrl saving screen share stream for after node reconnect');
                    if (oldFileplay) {
                        // so that it is saved and restored.
                        self.fileplay = oldFileplay;
                    }
                    self.saveScreenshareForReconnection();
                    self.stopScreenShare(false, 'node disconnected ');
                    if (self.screenshareState == 'disconnected') {
                        self.screenshareState = 'stopped'; // so that it waits from node server content status before starting screen, even on janus reconnect
                    }
                }
                self.leaveConferenceAlt('leavealt');

                notificationService.show(
                    localizationService.translate(
                        'SCREENSHARE.NOTIFICATIONS.PUBLISHED_MEDIA_STOPPED_AS_WE_ATTEMPT_TO_RECONNECT'
                    ),
                    'disconnected'
                );
            };

            if (self.nodeReconnectTimer) {
                logger.log('onNodeDisconnected', 'Consecutive node disconnect detected, clearing the previous timer');
                self.msgflowEvent('node disconnected:repeated', {
                    screenshare: self.screenshareState,
                    videoroom: !!self.videoroom
                });
                $timeout.cancel(self.nodeReconnectTimer);
                self.nodeReconnectTimer = null;
            }

            if (
                (self.sipcall && self.sipcall.state.ice != 'connected') ||
                (self.screenshare && self.screenshare.state.ice != 'connected')
            ) {
                self.msgflowEvent('node disconnected:now', {
                    screenshare: self.screenshareState,
                    videoroom: !!self.videoroom
                });
                reconnectHandler('immediate');
            } else {
                self.msgflowEvent('node disconnected', {
                    screenshare: self.screenshareState,
                    videoroom: !!self.videoroom
                });
                self.nodeReconnectTimer = $timeout(reconnectHandler, self.nodeReconnectTimeout);
                self.checkConnectivity('onNodeDisconnected'); // perform connectivity check too if disconnected only from node.
            }
        }

        function onNodeReconnected() {
            logger.log('videoroomCtrl on node reconnected, screen share state: ' + self.screenshareState);
            self.msgflowEvent('node reconnected');

            if (self.fastReconnectSocket.enabled) {
                self.fastReconnectSocket.connectTs = Date.now();
            }

            self.setVideoPublishState('button', false);
            self.setVideoPublishState('server', false);

            if (self.nodeReconnectTimer) {
                $timeout.cancel(self.nodeReconnectTimer);
                self.nodeReconnectTimer = null;
            } else if (self.nodeReconnectJoin) {
                logger.log('videoroomCtrl will rejoin conference after 1s');
                self.updateConnTimeData('mediaSocket', false);
                self.nodeReconnectJoinTimeout = $timeout(function () {
                    notificationService.show(
                        localizationService.translate('SCREENSHARE.NOTIFICATIONS.YOUR_PUBLISHED_MEDIA_WILL_RESTART'),
                        'connected'
                    );
                    self.joinConference();
                }, 1000);
                $timeout(cameraErrorCheckAndFix, 10000);
            }
            self.nodeReconnectJoin = false;

            if (self.screenshareOwner && self.screenshareOwner != self.attendantId) {
                self.msgflowEvent('play-restart:reconnect', {
                    id: self.screenshareOwner,
                    screen: true,
                    reason: 'on node reconnect'
                });
                self.restartVideoPlay(self.getScreenshareIdFromAttendantId(self.screenshareOwner));
            }

            self.updateConnTimeData('nodeSocket', true);
            self.updateE2EEStateIfNeeded();

            const fastReconnect = self.fastReconnectSocket;
            if (fastReconnect.enabled && fastReconnect.fastTs) {
                const reconnectDuration = Math.max(0, fastReconnect.connectTs - fastReconnect.disconnectTs);
                const triggerDiff = Math.max(0, fastReconnect.connectTs - fastReconnect.fastTs);
                logger.log(
                    'onNodeReconnected',
                    `fastReconnectSocket total=${reconnectDuration}ms, difference=${triggerDiff}ms`
                );
                // If reconnect is not enabled, it will log 'fast-reconnect:false' to msgflow; otherwise, it will log 'fast-reconnect'.
                self.msgflowEvent(`fast-reconnect${self.fastReconnectSocket.reconnect ? '' : ':false'}`, {
                    total: reconnectDuration,
                    difference: triggerDiff
                });
                fastReconnect.fastTs = fastReconnect.connectTs = fastReconnect.disconnectTs = null;
            }
        }

        // This checks for a specfic bug state.  Your local video feed is showing, however the local attendee service thinks the video
        // is muted and there is a device error for your cam.  In this case we are going to stop publishign
        function cameraErrorCheckAndFix() {
            logger.log('calling cameraErrorCheckAndFix after joinConference when node is reconnecting');
            if (
                avDeviceService.getSfuDeviceErrorFromId(storageService.get('video-device')) &&
                attendeeService.getIsVideoMuted() &&
                self.isLocalVideoElementShowing()
            ) {
                logger.warn('cameraErrorCheckAndFix', 'device error detected with possible publishing state');
                self.stopVideoPublish();
                self.removeVideoElement();
            }
        }

        // only called when missingType is set.
        function onNetworkStateChange(event) {
            logger.log('onNetworkStateChange', `type=${event.type}`);

            var now = Date.now();
            var connectionType = event.type === 'offline' ? 'none' : 'unknown';

            self.updateConnTimeData('network', event.type !== 'offline');
            self.logNetworkChange();

            if (connectionType !== 'none') {
                if (self.isNetworkConnectionTypeRestored(now, connectionType)) {
                    logger.log(
                        'onNetworkStateChange',
                        `type changed from ${
                            self.networkState.previousConnectionType
                        } to ${connectionType} after ${self.getNetworkConnectionTypeChangePeriod(now)}ms`
                    );

                    if (self.networkState.enabled) {
                        self.tryRejoinConferenceAfterNetworkChange('state-online');
                    }
                }
            }

            if (self.networkState.previousConnectionType !== connectionType) {
                self.networkState.previousConnectionType = connectionType;
                self.networkState.previousConnectionTypeChangeTime = now;
            }
        }

        // do not use type if missingType is set.
        function onNetworkChange(event) {
            const connection = $window.navigator.connection;
            if (connection) {
                logger.log(
                    'onNetworkChange',
                    `type=${self.networkState.previousConnectionType}=>${connection.type} effectiveType=${self.networkState.previousEffectiveType}=>${connection.effectiveType} downlink=${self.networkState.previousDownlink}=>${connection.downlink} rtt=${self.networkState.previousRtt}=>${connection.rtt};`
                );

                if (!self.networkState.missingType) {
                    self.updateConnTimeData('network', connection.type !== 'none');
                }

                $rootScope.$broadcast('network-change', event);

                var now = Date.now();

                if (!self.networkState.missingType && connection.type !== 'none') {
                    if (self.isNetworkConnectionTypeRestored(now)) {
                        logger.log(
                            'onNetworkChange',
                            `type changed from ${self.networkState.previousConnectionType} to ${
                                connection.type
                            } after ${self.getNetworkConnectionTypeChangePeriod(now)}ms`
                        );

                        self.networkState.waitForRestore = true;
                    } else if (self.isNetworkDownlinkRestored(now)) {
                        logger.log(
                            'onNetworkChange',
                            `downlink changed from ${self.networkState.previousDownlink} to ${
                                connection.downlink
                            } after ${self.getNetworkDownlinkChangePeriod(now)}ms`
                        );
                    } else if (self.isNetworkRttRestored(now)) {
                        logger.log(
                            'onNetworkChange',
                            `rtt changed from ${self.networkState.previousRtt} to ${
                                connection.rtt
                            } after ${self.getNetworkRttChangePeriod(now)}ms`
                        );
                    }
                }

                if (self.networkState.waitForRestore) {
                    self.networkState.waitForRestore = false;
                    if (self.networkState.enabled && connection.downlink > 0 && connection.rtt > 0) {
                        self.tryRejoinConferenceAfterNetworkChange('network-change');
                    }
                } else if (
                    self.networkState.enableRejoinAfterNetworkConditionsChanged &&
                    self.isNetworkConditionsChanged()
                ) {
                    logger.log('onNetworkChange', 'network conditions changed');

                    const pcInfo = self.getPeerConnectionForStats();
                    if (pcInfo && !pcInfo.pc.statsAnalysisInProgress) {
                        pcInfo.pc.statsAnalysisInProgress = true;
                        self.analyzePeerConnectionStatsAndRejoinConferenceIfNeeded(pcInfo.pc, pcInfo.type);
                    }
                }

                self.updatePreviousNetworkState(now, connection);
            }
        }

        function isNetworkConnectionTypeRestored(now, connectionType) {
            return (
                self.networkState.previousConnectionType === 'none' &&
                (connectionType || $window.navigator.connection?.type) !== 'none' &&
                self.getNetworkConnectionTypeChangePeriod(now) > self.networkState.restorePeriod
            );
        }

        function getNetworkConnectionTypeChangePeriod(now) {
            if (!self.networkState.previousConnectionTypeChangeTime) {
                return 0;
            }

            return now - self.networkState.previousConnectionTypeChangeTime;
        }

        function isNetworkDownlinkRestored(now) {
            return (
                self.networkState.previousDownlink === 0 &&
                $window.navigator.connection?.downlink > 0 &&
                self.getNetworkDownlinkChangePeriod(now) > self.networkState.restorePeriod
            );
        }

        function getNetworkDownlinkChangePeriod(now) {
            if (!self.networkState.previousDownlinkChangeTime) {
                return 0;
            }

            return now - self.networkState.previousDownlinkChangeTime;
        }

        function isNetworkRttRestored(now) {
            return (
                self.networkState.previousRtt === 0 &&
                $window.navigator.connection?.rtt > 0 &&
                self.getNetworkRttChangePeriod(now) > self.networkState.restorePeriod
            );
        }

        function getNetworkRttChangePeriod(now) {
            if (!self.networkState.previousRttChangeTime) {
                return 0;
            }

            return now - self.networkState.previousRttChangeTime;
        }

        function isNetworkConditionsChanged() {
            return (
                (!self.networkState.missingType &&
                    self.networkState.previousConnectionType !== $window.navigator.connection?.type) ||
                self.networkState.previousEffectiveType !== $window.navigator.connection?.effectiveType ||
                self.isNetworkDownlinkChangedSignificantly() ||
                self.isNetworkRTTChangedSignificantly()
            );
        }

        function isNetworkDownlinkChangedSignificantly() {
            // downlink significantly changed within 4g range
            // https://developer.mozilla.org/en-US/docs/glossary/effective_connection_type

            if (self.networkState.previousDownlink == null) {
                return false;
            }

            return (
                (self.networkState.previousDownlink < self.networkState.downlinkThresholdAffectsQuality &&
                    $window.navigator.connection?.downlink >= self.networkState.downlinkThresholdAffectsQuality) ||
                (self.networkState.previousDownlink >= self.networkState.downlinkThresholdAffectsQuality &&
                    $window.navigator.connection?.downlink < self.networkState.downlinkThresholdAffectsQuality)
            );
        }

        function isNetworkRTTChangedSignificantly() {
            if (self.networkState.previousRtt == null) {
                return false;
            }

            let previousRttRange = self.getNetworkRttRange(self.networkState.previousRtt);
            let rttRange = self.getNetworkRttRange($window.navigator.connection?.rtt);

            return previousRttRange !== rttRange;
        }

        function getNetworkRttRange(rtt) {
            // low: rtt < 100
            // medium: 100 <= rtt < 200
            // high: rtt >= 200

            if (rtt < self.networkState.rttLow) {
                return 'low';
            } else if (rtt < self.networkState.rttHigh) {
                return 'medium';
            } else {
                return 'high';
            }
        }

        function updatePreviousNetworkState(now, connection) {
            if (!self.networkState.missingType && self.networkState.previousConnectionType !== connection.type) {
                self.networkState.previousConnectionType = connection.type;
                self.networkState.previousConnectionTypeChangeTime = now;
            }

            if (self.networkState.previousDownlink !== connection.downlink) {
                self.networkState.previousDownlink = connection.downlink;
                self.networkState.previousDownlinkChangeTime = now;
            }

            if (self.networkState.previousRtt !== connection.rtt) {
                self.networkState.previousRtt = connection.rtt;
                self.networkState.previousRttChangeTime = now;
            }

            if (self.networkState.previousEffectiveType !== connection.effectiveType) {
                self.networkState.previousEffectiveType = connection.effectiveType;
            }
        }

        function getPeerConnectionForStats() {
            var attendantId = attendeeService.getAttendantId();
            var attendee = attendeeListService.findAttendee(attendantId);

            let pc = undefined;
            let pcType = undefined;

            if (attendee.switchedToPhone) {
                if (self.videoroom) {
                    pc = self.videoroom?.webrtcStuff?.pc;
                    pcType = 'videoroom';
                } else if (self.screenshare) {
                    pc = self.screenshare?.webrtcStuff?.pc;
                    pcType = 'screenshare';
                } else if (self.feeds && Object.keys(self.feeds).length > 0) {
                    // TODO: ANYAM-96394: get active PC from feeds. Need to find the right way in case of LGV (only part of feeds are active).
                    // pc = self.feeds[Object.keys(self.feeds)[0]]?.webrtcStuff?.pc;
                }
            } else {
                pc = self.sipcall?.webrtcStuff?.pc;
                pcType = 'audio';
            }

            if (
                !pc ||
                !pc.getStats ||
                (self.networkState.allowedStatsTypes && !self.networkState.allowedStatsTypes.includes(pcType))
            ) {
                logger.warn(
                    'getPeerConnectionForStats',
                    `Invalid pc for stats. Calculated stats type: ${pcType}; allowed stats types: ${self.networkState.allowedStatsTypes}`
                );
                return undefined;
            }

            return { pc: pc, type: pcType };
        }

        function cancelPeerConnectionStats() {
            if (self.peerConnectionStatsTimer) {
                $timeout.cancel(self.peerConnectionStatsTimer);
                self.peerConnectionStatsTimer = null;
            }
        }

        function analyzePeerConnectionStatsAndRejoinConferenceIfNeeded(pc, statsType, moreStatsOptions) {
            self.getPeerConnectionStats(pc, statsType, stats => {
                logger.log('analyzePeerConnectionStatsAndReconnectIfNeeded', 'got peer connection stats', stats);

                if (stats) {
                    self.peerConnectionStatsTimer = $timeout(() => {
                        self.getPeerConnectionStats(pc, statsType, newStats => {
                            self.peerConnectionStatsTimer = null;

                            logger.log(
                                'analyzePeerConnectionStatsAndReconnectIfNeeded',
                                `got peer connection stats after period (${self.networkState.timeBetweenGetPCStats} ms)`,
                                newStats
                            );

                            // for testing
                            if (stats && newStats) {
                                stats = self.applyFakePeerConnectionStats(stats, self.networkState.fakePrevStats);
                                newStats = self.applyFakePeerConnectionStats(newStats, self.networkState.fakeNewStats);
                            }

                            const shouldReconnect = self.shouldRejoinConference(statsType, stats, newStats);
                            delete pc.statsAnalysisInProgress;

                            logger.log(
                                'analyzePeerConnectionStatsAndReconnectIfNeeded',
                                `after analyze stats diff, shouldReconnect=${shouldReconnect}; downlink=${$window.navigator.connection?.downlink}; rtt=${$window.navigator.connection?.rtt}`
                            );

                            self.networkCancelMoreStatsAfter();

                            if (!shouldReconnect) {
                                self.networkCollectMoreStatsAfter(undefined, moreStatsOptions);
                            } else if (
                                $window.navigator.connection?.downlink > 0 &&
                                $window.navigator.connection?.rtt > 0
                            ) {
                                self.tryRejoinConferenceAfterNetworkChange('connection-stats');
                            }
                        });
                    }, self.networkState.timeBetweenGetPCStats);
                } else {
                    delete pc.statsAnalysisInProgress;
                }
            });
        }

        function networkCancelMoreStatsAfter() {
            if (self.networkMoreStatsTimer) {
                $timeout.cancel(self.networkMoreStatsTimer);
                self.networkMoreStatsTimer = null;
            }
        }

        function networkCollectMoreStatsAfter(delay, options) {
            if (!self.networkState.moreStats.enableCheckConnectivity && !self.networkState.moreStats.enableSpeedtest) {
                return;
            }

            self.networkMoreStatsTimer = $timeout(
                () => {
                    self.networkMoreStatsTimer = null;

                    if (self.networkState.moreStats.enableCheckConnectivity && !options?.skipCheckConnectivity) {
                        self.checkConnectivity('networkCollectMoreStatsAfter');
                    }

                    if (self.networkState.moreStats.enableSpeedtest && !options?.skipSpeedTest) {
                        const startTime = Date.now();
                        self.startSpeedTestDownload(() => {
                            self.logSpeedTestResult('networkCollectMoreStatsAfter', Math.round(Date.now() - startTime));
                        });
                    }
                },
                delay !== undefined ? delay : self.networkState.moreStats.collectAfter
            );
        }

        function tryRejoinConferenceAfterNetworkChange(reason = 'unknown') {
            if (self.nodeReconnectJoin) {
                logger.log(
                    'tryRejoinConferenceAfterNetworkChange',
                    'not doing janus reconnect, waiting for node websocket to connect, reason=' + reason
                );
                if (self.fastReconnectSocket.enabled) {
                    if (!socketService.socket || socketService.isConnected()) {
                        logger.log(
                            'tryRejoinConferenceAfterNetworkChange',
                            `fastReconnectSocket ignored socket=${!!socketService.socket} connected=${socketService.isConnected()}`
                        );
                    } else {
                        const fastReconnect = self.fastReconnectSocket;
                        fastReconnect.fastTs = Date.now();

                        if (!fastReconnect.reconnect) {
                            logger.log(
                                'tryRejoinConferenceAfterNetworkChange',
                                'fastReconnectSocket ignored actual reconnect'
                            );
                        } else {
                            logger.log('tryRejoinConferenceAfterNetworkChange', 'fastReconnectSocket started');

                            try {
                                socketService.socket.disconnect();
                            } catch (err) {
                                logger.warn(
                                    'tryRejoinConferenceAfterNetworkChange',
                                    'Error in node socket disconnect',
                                    err
                                );
                            }
                            try {
                                socketService.socket.connect();
                            } catch (err) {
                                logger.warn(
                                    'tryRejoinConferenceAfterNetworkChange',
                                    'Error in node socket connection',
                                    err
                                );
                            }
                        }
                    }
                }
            } else {
                logger.log('tryRejoinConferenceAfterNetworkChange', 'doing janus reconnect, reason=' + reason);
                self.rejoinConferenceAfter('network change', self.networkState.rejoinAfter, true, true);
            }
        }

        function checkSwitchToAllRelay(result) {
            const relaySwitch = self.fixSwitchToAllRelay;
            if (relaySwitch.enabled && !relaySwitch.useRelay) {
                const withRelay = result.filter(item => item.localCandidate?.candidateType === 'relay').length;
                const withoutRelay = result.filter(
                    item => item.localCandidate?.candidateType && item.localCandidate?.candidateType !== 'relay'
                ).length;
                logger.log('checkSwitchToAllRelay', `Candidate count: relay=${withRelay}, non-relay=${withoutRelay}`);
                if (
                    withoutRelay > 0 &&
                    withRelay > 0 &&
                    withRelay >= Math.floor((withoutRelay + withRelay) * relaySwitch.threshold)
                ) {
                    relaySwitch.count++;
                    if (relaySwitch.count >= relaySwitch.maxCount) {
                        relaySwitch.count = 0;
                        relaySwitch.useRelay = true;
                        logger.log('checkSwitchToAllRelay', 'Switching to relay candidate');
                        return true;
                    }
                } else {
                    relaySwitch.count = 0;
                }
            }
            return false;
        }
    }
]);
